import {
  CustomerSearchDataFragment,
  CustomerType,
  useSearchCustomersLazyQuery
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import {
  Color,
  FilterAltIcon,
  MagnifyingGlassIcon,
  MultiSelect,
  TextField
} from "@bonliva-crm/ui";
import { useGlobalStore } from "@hooks";
import { Box, makeStyles } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { CustomerCardList } from "../components/customer-card-list";

type Option = {
  label: string;
  value: string;
};

export type CustomerFullSearchValue = CustomerSearchDataFragment;

type Props<
  IsMulti extends boolean,
  T = IsMulti extends true ? CustomerFullSearchValue[] : CustomerFullSearchValue | null
> = {
  value: T;
  onChange: (value: T) => void;
  isMulti: IsMulti;
  customerTypes?: CustomerType[];
  instantSelection?: IsMulti extends true ? never : boolean;
};

export const CustomerFullSearch = <IsMulti extends boolean>(props: Props<IsMulti>) => {
  const classes = useStyles();

  const { cache } = useGlobalStore();
  const [debounceLoading, setDebounceLoading] = useState(false);

  const customerGroups = cache.customerGroups.map((g) => ({
    label: g,
    value: g
  }));

  const [searchCustomersQuery, { loading, data }] = useSearchCustomersLazyQuery({
    fetchPolicy: "network-only"
  });

  const [searchText, setSearchText] = useState("");
  const [searchCustomerNumber, setSearchCustomerNumber] = useState("");
  const [searchFilters, setSearchFilters] = useState<Option[]>([]);
  const emptySearch = !searchText && !searchCustomerNumber && searchFilters.length === 0;

  const timer = useRef(null);

  useEffect(() => {
    if (emptySearch) {
      setDebounceLoading(false);
      clearTimeout(timer.current);
      return;
    }
    setDebounceLoading(true);

    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      searchCustomersQuery({
        variables: {
          filter: {
            text: searchText,
            fullTextSearch: true,
            customerNumbers: searchCustomerNumber ? [searchCustomerNumber] : [],
            group: searchFilters.map((s) => s.value),
            type: props.customerTypes
          },
          skip: 0,
          limit: 15
        }
      });
      setDebounceLoading(false);
    }, 500);

    return () => clearTimeout(timer.current);
  }, [searchText, searchCustomerNumber, searchFilters]);

  return (
    <Box className={classes.root}>
      <Box className={classes.inputContainer}>
        <TextField
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={locale.searchForCustomerName}
          InputProps={{ startAdornment: <MagnifyingGlassIcon /> }}
          fullWidth={true}
          autoFocus={true}
        />
        <MultiSelect
          options={customerGroups}
          value={searchFilters}
          onChange={(v) => setSearchFilters(v.slice())}
          hideSelectedOptions={false}
          optionType="checkbox"
          styleProps={{
            control: { showBorder: true },
            menu: { showBorder: true },
            container: { height: "2.5rem" },
            placeholder: { color: Color.Body, fontSize: "1rem" }
          }}
          placeholder={locale.CustomerGroup}
          showOptionDivider={false}
          hidePlaceholderOnFocus={false}
          controlShouldRenderValue={false}
          closeMenuOnSelect={false}
          dropDownIndicatorIcon={<FilterAltIcon />}
          isSearchable={false}
          showBadge={true}
        />
        <TextField
          label={locale.CustomerNumber}
          value={searchCustomerNumber}
          onChange={(e) => setSearchCustomerNumber(e.target.value)}
          fullWidth={true}
          style={{ gridColumn: "1 / -1" }}
        />
      </Box>
      <CustomerCardList
        customers={emptySearch ? [] : data?.customers.items || []}
        selectedCustomers={props.value}
        setSelectedCustomers={props.onChange}
        isLoading={loading || debounceLoading}
        isEmptySearch={emptySearch}
        isMulti={props.isMulti}
        instantSelection={props.instantSelection}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingRight: theme.spacing(4)
  },
  customerContainer: {
    maxHeight: "600px",
    overflow: "auto"
  },
  inputContainer: {
    display: "grid",
    gridTemplateColumns: "70% 30%",
    gridTemplateRows: "1fr 1fr",
    gap: theme.spacing(2)
  }
}));
